import { BaseModel } from './base-model';
import { SelectOption } from '.';

export enum ProfileTab {
  Credentials = 0,
  LimitAndFees = 1,
  BankInformation = 2,
  ProcessorInformation = 3,
  ContactInformation = 4,
  Permissions = 5,
  Users = 6,
  Documents = 7,
  Summary = 8,
  Deployment = 9,
  ActivityLogs = 10,
  Resolve = 11,
}

export interface ProfileAddress {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export enum MerchantProfileStatus {
  Archived = 0,
  Active = 1,
  Draft = 2,
  Disabled = 3,
  PendingVerification = 4,
}

export const MerchantProfileStatusLabels = {};
MerchantProfileStatusLabels[MerchantProfileStatus.Archived] = 'Archived';
MerchantProfileStatusLabels[MerchantProfileStatus.Active] = 'Active';
MerchantProfileStatusLabels[MerchantProfileStatus.Draft] = 'Draft';
MerchantProfileStatusLabels[MerchantProfileStatus.Disabled] = 'Disabled';
MerchantProfileStatusLabels[MerchantProfileStatus.PendingVerification] = 'Pending Verification';

// Labels for the tabs the merchant profile page uses; the other profile types label their own
export const ProfileTabLabels = {};
ProfileTabLabels[ProfileTab.Credentials] = 'Credentials';
ProfileTabLabels[ProfileTab.Permissions] = 'User Permissions';
ProfileTabLabels[ProfileTab.ActivityLogs] = 'Activity Logs';
ProfileTabLabels[ProfileTab.Summary] = 'Summary';
ProfileTabLabels[ProfileTab.Deployment] = 'Deployment';

// A value shape the backend enforces; the hint completes "<Field> must be ..." in the error message
export interface FormatRule {
  pattern?: RegExp;
  length?: number;
  hint: string;
}

export function getFormatRuleMessage(label: string, rule: FormatRule): string {
  return label + ' must be ' + rule.hint + '.';
}

// For a dropdown whose saved value is no longer one of its options
export const ListOptionRule: FormatRule = { hint: 'one of the listed options' };

export const MerchantCategoryCodeRule: FormatRule = { pattern: /^[0-9]{4}$/, hint: 'exactly 4 digits' };

// Industry codes Mastercard accepts for Resolve; the API takes the 3 letter code, the label is for display
export const ResolveIndustryCodeOptions: SelectOption[] = [
  { value: 'AAC', label: "Children's Apparel" },
  { value: 'AAF', label: 'Family Apparel' },
  { value: 'AAM', label: "Men's Apparel" },
  { value: 'AAW', label: "Women's Apparel" },
  { value: 'AAX', label: 'Miscellaneous Apparel' },
  { value: 'ACC', label: 'Accommodations' },
  { value: 'ACS', label: 'Automotive New and Used Car Sales' },
  { value: 'ADV', label: 'Advertising Services' },
  { value: 'AFH', label: 'Agriculture/Forestry/Fishing/Hunting' },
  { value: 'AFS', label: 'Automotive Fuel' },
  { value: 'ALS', label: 'Accounting and Legal Services' },
  { value: 'ANC', label: 'Automotive New Car' },
  { value: 'ARA', label: 'Amusement, Recreation Activities' },
  { value: 'ART', label: 'Arts and Craft Stores' },
  { value: 'AUC', label: 'Automotive Used Only Car Sales' },
  { value: 'AUT', label: 'Automotive Retail' },
  { value: 'BKS', label: 'Book Stores' },
  { value: 'BMV', label: 'Music and Videos' },
  { value: 'BNM', label: 'Newspapers and Magazines' },
  { value: 'BTN', label: 'Bars/Taverns/Nightclubs' },
  { value: 'BWL', label: 'Beer/Wine/Liquor Stores' },
  { value: 'CCR', label: 'Consumer Credit reporting' },
  { value: 'CEA', label: 'Consumer Electronics / Appliances' },
  { value: 'CES', label: 'Cleaning and exterminating Services' },
  { value: 'CGA', label: 'Casino and Gambling Activities' },
  { value: 'CMP', label: 'Computer / Software Stores' },
  { value: 'CNS', label: 'Construction Services' },
  { value: 'COS', label: 'Cosmetics and Beauty Services' },
  { value: 'CPS', label: 'Camera/Photography Supplies' },
  { value: 'CSV', label: 'Courier Services' },
  { value: 'CTC', label: 'Communications / TeleCommunications / Cable' },
  { value: 'CTE', label: 'Communications, Telecommunications Equipment' },
  { value: 'CTS', label: 'Communications, Telecommunications, Cable Services' },
  { value: 'CUE', label: 'College, University Education' },
  { value: 'CUF', label: 'Clothing, Uniform, Costume Rental' },
  { value: 'DAS', label: 'Dating Services' },
  { value: 'DCS', label: 'Death Care Services' },
  { value: 'DIS', label: 'Discount Department Stores' },
  { value: 'DLS', label: 'Dry Cleaning, Laundry Services' },
  { value: 'DPT', label: 'Department Stores' },
  { value: 'DSC', label: 'Drug Store Chains' },
  { value: 'DVG', label: 'Variety / General Merchandise Stores' },
  { value: 'EAP', label: 'Eating Places' },
  { value: 'ECA', label: 'Employment, Consulting Agencies' },
  { value: 'EHS', label: 'Elementary, Middle, High Schools' },
  { value: 'EQR', label: 'Equipment Rental' },
  { value: 'ETC', label: 'Miscellaneous' },
  { value: 'FLO', label: 'Florists' },
  { value: 'FSV', label: 'Financial Services' },
  { value: 'GHC', label: 'Giftware/Houseware/Card Shops' },
  { value: 'GRO', label: 'Grocery Stores' },
  { value: 'GSF', label: 'Specialty Food Stores' },
  { value: 'HBM', label: 'Health/Beauty/Medical Supplies' },
  { value: 'HCS', label: 'Health Care and Social Assistance' },
  { value: 'HFF', label: 'Home Furnishings / Furniture' },
  { value: 'HGH', label: 'Houseware / Giftware / Home Solutions' },
  { value: 'HIC', label: 'Home Improvement Centers' },
  { value: 'INS', label: 'Insurance' },
  { value: 'INV', label: 'Not Valid (No Data)' },
  { value: 'IRS', label: 'Information Retrieval Services' },
  { value: 'JGS', label: 'Jewelry and Giftware' },
  { value: 'LEE', label: 'Live Performances, Events, Exhibits' },
  { value: 'LLS', label: 'Luggage and Leather Stores' },
  { value: 'MAS', label: 'Miscellaneous Administrative and Waste Disposal Services' },
  { value: 'MER', label: 'Miscellaneous entertainment and recreation' },
  { value: 'MES', label: 'Miscellaneous Educational Services' },
  { value: 'MFG', label: 'Manufacturing' },
  { value: 'MOS', label: 'Miscellaneous Personal Services' },
  { value: 'MOT', label: 'Movie and Other Theatrical' },
  { value: 'MPI', label: 'Miscellaneous Publishing Industries' },
  { value: 'MPS', label: 'Miscellaneous Professional Services' },
  { value: 'MRS', label: 'Maintenance and Repair Services' },
  { value: 'MTS', label: 'Miscellaneous Technical Services' },
  { value: 'MVS', label: 'Miscellaneous Vehicle Sales' },
  { value: 'NOU', label: 'Not Used (No Locations)' },
  { value: 'NSA', label: 'Non-store Apparel / Accessory / Footwear' },
  { value: 'NSB', label: 'Non-store Books, Music and Videos' },
  { value: 'NSC', label: 'Non-store Computers / Software' },
  { value: 'NSD', label: 'Non-store Department Stores' },
  { value: 'NSE', label: 'Non-store Consumer Electronics / Appliances' },
  { value: 'NSF', label: 'Non-store Food / Perishables' },
  { value: 'NSH', label: 'Non-store Home Furnishings / Houseware / Giftware' },
  { value: 'NSM', label: 'Non-store General Merchandise' },
  { value: 'NSO', label: 'Non-store Office Supply Chains' },
  { value: 'NSP', label: 'Non-store Pharmaceutical / Health / Beauty' },
  { value: 'NSS', label: 'Non-store Sporting Goods / Apparel / Footwear' },
  { value: 'NST', label: 'Non-store TV Shopping' },
  { value: 'NSX', label: 'Non-store Miscellaneous' },
  { value: 'NSY', label: 'Non-store Toy Stores' },
  { value: 'OBS', label: 'Obsolete' },
  { value: 'OPT', label: 'Optical' },
  { value: 'OSC', label: 'Office Supply Chains' },
  { value: 'PET', label: 'Pet Stores' },
  { value: 'PFS', label: 'Photofinishing Services' },
  { value: 'PHS', label: 'Photography Services' },
  { value: 'PST', label: 'Professional Sports Teams' },
  { value: 'PUA', label: 'Public Administration' },
  { value: 'RCP', label: 'Religious, Civic and Professional Organizations' },
  { value: 'RES', label: 'Real Estate Services' },
  { value: 'SGS', label: 'Sporting Goods / Apparel / Footwear' },
  { value: 'SHS', label: 'Shoe Stores' },
  { value: 'SND', label: 'Software Production, Network Services and Data Processing' },
  { value: 'SSS', label: 'Security, Surveillance Services' },
  { value: 'TAT', label: 'Travel Agencies and Tour Operators' },
  { value: 'TEA', label: 'T+E Airlines' },
  { value: 'TEB', label: 'T+E Bus' },
  { value: 'TEC', label: 'T+E Car Rental' },
  { value: 'TEO', label: 'T+E Other' },
  { value: 'TER', label: 'T+E Restaurants' },
  { value: 'TET', label: 'T+E Cruise Lines' },
  { value: 'TEV', label: 'T+E Vehicle Rental' },
  { value: 'TOY', label: 'Toy Stores' },
  { value: 'TRR', label: 'T+E Railroad' },
  { value: 'TSS', label: 'Other Transportation Services' },
  { value: 'TTL', label: 'T+E Taxi and Limousine' },
  { value: 'UNK', label: 'Unknown' },
  { value: 'UTL', label: 'Utilities' },
  { value: 'VES', label: 'Veterinary Services' },
  { value: 'VGR', label: 'Video and Game Rentals' },
  { value: 'VTB', label: 'Vocation, Trade and Business Schools' },
  { value: 'WAH', label: 'Warehouse' },
  { value: 'WHC', label: 'Wholesale Clubs' },
  { value: 'WHT', label: 'Wholesale Trade' },
];

// Collapsible cards on the credentials tab; the values are the keys of the component's sections map
export enum MerchantProfileSection {
  MerchantProfile = 'merchantProfile',
  Business = 'business',
  AccountDetails = 'accountDetails',
  Contacts = 'contacts',
}

export enum MerchantProfileContactType {
  Technical = 'technical',
  Risk = 'risk',
}

export interface MerchantProfileContact {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export enum MerchantProfileContactField {
  FirstName = 'first_name',
  LastName = 'last_name',
  Email = 'email',
  Phone = 'phone',
}

// The values double as the status chip's CSS class
export enum ChipTone {
  None = 'none',
  Verified = 'verified',
  Pending = 'pending',
  Disabled = 'disabled',
  Failed = 'failed',
}

export interface ProductChip {
  label: string;
  tone: ChipTone;
}

export enum AchField {
  AccountNumber = 'account_number',
  Name = 'name',
  AccountHolderType = 'account_holder_type',
}

export interface MultiSelectOption<T = number> {
  id: T;
  text: string;
}

export enum ResolveBusinessType {
  SoleProprietorship = 1,
  BusinessCorporation = 2,
  Other = 3,
}

export const ResolveBusinessTypeLabels = {};
ResolveBusinessTypeLabels[ResolveBusinessType.SoleProprietorship] = 'Sole Proprietorship';
ResolveBusinessTypeLabels[ResolveBusinessType.BusinessCorporation] = 'Business Corporation';
ResolveBusinessTypeLabels[ResolveBusinessType.Other] = 'Other';

export enum ResolveSyncStatus {
  Configuration = 1,
  Testing = 2,
  Live = 3,
  Inactive = 4,
  LockedForEdit = 5,
}

export const ResolveSyncStatusLabels = {};
ResolveSyncStatusLabels[ResolveSyncStatus.Configuration] = 'Configuration';
ResolveSyncStatusLabels[ResolveSyncStatus.Testing] = 'Testing';
ResolveSyncStatusLabels[ResolveSyncStatus.Live] = 'Live';
ResolveSyncStatusLabels[ResolveSyncStatus.Inactive] = 'Inactive';
ResolveSyncStatusLabels[ResolveSyncStatus.LockedForEdit] = 'Locked for edit';

// One ARID/CAID pair; either half may be null, but not both
export interface AcquirerMerchantId {
  acquirer_bin: string;
  card_acceptor_id: string;
}

export interface MerchantProfileResolve extends BaseModel {
  merchant_id: string;
  descriptors: string[];
  acquirer_merchant_ids: AcquirerMerchantId[];
  industry_code: string;
  is_enabled: boolean;
  business_type: ResolveBusinessType;
  domain: string | number;
  crms: number[];
  location_id: string;
  etag: string;
  sync_status: ResolveSyncStatus;
  synced: string;
}

export interface MerchantProfile extends BaseModel {
  name: string;
  description: string;
  campaign: string | number;
  category: number;
  address: ProfileAddress;
  business_contact: MerchantProfileContact;
  technical_contact: MerchantProfileContact;
  emergency_contact: MerchantProfileContact;
  url: string;
  merchant_category_code: string;
  payment_source: number;
  created: string;
  is_deleted: boolean;
  status: MerchantProfileStatus;
  resolve: MerchantProfileResolve;
}

export interface MerchantProfileListItem extends BaseModel {
  name: string;
  status: MerchantProfileStatus;
  merchant_category_code: string;
  category: string | number;
  url: string;
  description: string;
  last_verified?: string;
  last_updated?: string;
  showThreeDotMenu?: boolean;
}

export interface MerchantProfileResolveFormValue {
  id?: string | number;
  descriptors: string[];
  acquirer_merchant_ids: AcquirerMerchantId[];
  industry_code: string;
  is_enabled: boolean;
  business_type: ResolveBusinessType;
  domain: string | number;
  crms: MultiSelectOption[];
}

export type MerchantProfileFormValue = Omit<MerchantProfile, 'resolve'>;

export type MerchantProfileResolvePayload = Partial<MerchantProfileResolve>;

export interface MerchantProfilePayload extends Partial<Omit<MerchantProfile, 'resolve'>> {
  resolve?: MerchantProfileResolvePayload;
}

export interface MerchantProfileCategory extends BaseModel {
  name: string;
}
